import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useGetAssignmentVocabularyMutation } from '../../../../../features/authentication/assignApiSlice';
import 'rc-slider/assets/index.css';
import PrintOptions from './PrintOptions';
import PrintContainer from './PrintContainer';
import PreviewHeader from './PreviewHeader';
import { useGetPrintOptionsMutation, useUpdatePrintOptionsMutation } from '../../../../../features/authentication/persistApiSlice';
import { DeStackSetData, StackSetData, ResetSettings } from '../../../../myContent/utils/displaySetFunctions';
import ConfirmationModal from '../../../../../components/ConfirmationPopUp';
import { useCreateFlashcardSetMutation } from '../../../../../features/authentication/qSetApiSlice';
import CustomToast from '../../../../../components/Toast';
import create from '../../../../../../src/assets/images/Create.png';
import ActivityTypes from '../../../../../enums/activityTypes';

const VocabularyPrinting = () => {
  const location = useLocation();
  const { assignmentId, assignmentName } = location.state;
  const { activityType } = useParams();
  const [vocabulary, setVocabulary] = useState({});
  const [confirmModal, setConfirmModal] = useState(false);
  const [vocabularyData, setVocabularyData] = useState([]);
  const [fontStyleClass, setFontStyleClass] = useState('times');
  const [displayTopValue, setDisplayTopValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [printOptions, setPrintOptions] = useState({});
  const [originalSettingsData, setOriginalSettingsData] = useState({});
  const [contentHeights, setContentHeights] = useState([]);
  const [hideDuplicate, setHideDuplicate] = useState(false);
  const standardPageHeight = 1300;

  const [getPrintOptions] = useGetPrintOptionsMutation();
  const [updatePrintOptions] = useUpdatePrintOptionsMutation();
  const [assignmentVocabulary] = useGetAssignmentVocabularyMutation();
  const [createFlashcardSet, createFlashcardSetStatus] = useCreateFlashcardSetMutation();

  const getPrintOptionsAsync = async () => {
    const printOptionsResponse = await getPrintOptions().unwrap();

    let options = {
      ...printOptionsResponse, 
      imageCompressionPercent: printOptionsResponse?.imageCompressionPercent > 0 ? printOptionsResponse.imageCompressionPercent : 50
    };

    setOriginalSettingsData(options);
    setPrintOptions({ ...printOptions, ...options });
  };

  const updatePrintOptionsAsync = async (options) => {
    try {
      await updatePrintOptions(options).unwrap();
    } catch (ex) {
      console.log(ex);
    }
  };

  const getAssignmentVocabularyAsync = async () => {
    setIsLoading(true);
    const options = {
      assignmentId: assignmentId
    };
    const getAssignmentVocabularyresponse = await assignmentVocabulary(options).unwrap();
    setVocabulary(getAssignmentVocabularyresponse);
    setVocabularyData(getAssignmentVocabularyresponse.vocabularyInfo);
    setIsLoading(false);
  };

  const handleVocabularySelect = (event) => {
    const { name, checked } = event.target;
    if (name === 'selectAll') {
      const data = vocabularyData?.map((item) => {
        return { ...item, isChecked: checked };
      });
      setVocabularyData(data);
    } else {
      let vocabularyArray = vocabularyData?.map((item) =>
        `vocabulary${item.id}` === name ? { ...item, isChecked: checked } : item
      );
      setVocabularyData(vocabularyArray);
    }
  };

  const handleClearAll = () => {
    const data = vocabularyData?.map((item) => {
      return { ...item, isChecked: false };
    });
    setVocabularyData(data);
  };

  useEffect(() => {
    getPrintOptionsAsync();
    getAssignmentVocabularyAsync();
  }, []);

  useEffect(() => {
    if (contentHeights.length > 0) {
      setHideDuplicate(true);
    }
  }, [contentHeights]);

  const getContentHeight = (id) => {
    const item = contentHeights.find(q => q.id === id);
    return item ? item.height : 0;
  };

  const updateSettings = (data) => {
    setPrintOptions({ ...printOptions, ...data });
    updatePrintOptionsAsync({ ...printOptions, ...data });
  };

  const resetSettings = () => {
    let updatedSettings = ResetSettings(originalSettingsData);
    setPrintOptions(updatedSettings);
    updateSettings(updatedSettings);
  };

  useEffect(() => {
    if (!vocabularyData || vocabularyData?.length === 0) return;

    if (printOptions.twoColumn) {
      const updatedData = StackSetData(vocabularyData, { getContentHeight, standardPageHeight });
      setVocabularyData(updatedData);
    } else {
      const updatedData = DeStackSetData(vocabularyData);
      setVocabularyData(updatedData);
    }
  }, [printOptions?.twoColumn, vocabularyData]);

  const handleCreateFlashcard = async () => {
    const options = {
      assignmentId:assignmentId
    };
    const response = await createFlashcardSet(options);
    if(!response.error)
      CustomToast('Flashcard Set created Successfully','success');
    setConfirmModal(false);
  };

  return (
    <>
      <div className="vocabulary-container">
        <div className="vocabulary-header row">
          <div className="col-lg-9">
            <PreviewHeader
              printLabel={'Display/Print Vocabulary'}
              isLoading={isLoading}
              isVocabuloryPrint={activityType != ActivityTypes.FlashCard}
              onCreateFlashcard={()=> setConfirmModal(true)}
            />
            <div className="print-content-scroll">
              <PrintContainer
                assignmentName={assignmentName}
                displayTopValue={displayTopValue}
                fontStyleClass={fontStyleClass}
                printOptions={printOptions}
                isLoading={isLoading}
                teacherName={vocabulary?.teacherName}
                vocabularyData={vocabularyData}
                handleVocabularySelect={handleVocabularySelect}
                handleClearAll={handleClearAll}
                setContentHeights={setContentHeights}
                hideDuplicate={hideDuplicate}
                activityType={activityType}
              />
            </div>
          </div>
          <div className="col-lg-3">
            <PrintOptions
              assignmentName={assignmentName}
              displayTopValue={displayTopValue}
              setDisplayTopValue={setDisplayTopValue}
              setFontStyleClass={setFontStyleClass}
              printOptions={printOptions}
              isLoading={isLoading}
              updateSettings={updateSettings}
              resetSettings={resetSettings}
              activityType= {activityType}
            />
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={confirmModal}
        onClose={() => setConfirmModal(false)}
        onSave={() => handleCreateFlashcard()}
        titleName='Create Flashcard'
        contentWidth={'50%'}
        buttonPosition={'center'}
        content={`A Flashcard will be auto-created with these terms and definitions.<br/> It will appear in My Contents as "Vocab from ${assignmentName}".`}
        cancelButton={'Cancel'}
        confirmButton={'Create'}
        showIcon={true}
        icon={create}
        isLoading={createFlashcardSetStatus.isLoading}
      />
    </>
  );
};

export default VocabularyPrinting;
